import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	SelectInput,
	SELECT_MENU_PROPS,
} from "frame/components";
import {
	Card,
	CardContent,
	Grid,
	Typography,
	Modal,
	TextField,
	MenuItem,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import clsx from "clsx";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
} from "frame/components";
import { UploadFormCard } from "features/forms";
import { ValidatorForm, TextValidator, SelectValidator } from "react-material-ui-form-validator";

const useStyles = makeStyles((theme) => ({
	modalCard: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		outline: "none",
		boxShadow: theme.shadows[20],
		width: 900,
		maxHeight: "90vh",
		overflowY: "auto",
		maxWidth: "100%",
	},
	modalGridContainer: {
		marginTop: theme.spacing(3),
	},
	actions: {
		marginTop: theme.spacing(5),
		justifyContent: "flex-end",
	},
}));

const EquipmentModal = ({
	equipment,
	isOpen,
	onSubmit,
	onClose,
	readOnly = false
}) => {
	const classes = useStyles();
	const [equipmentForm, setEquipmentForm] = useState();
	const siteList = useSelector(({ sites }) => sites.pagination?.results || []);
	const currentOrganisationId = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);

	// Equipment type options
	const equipmentTypeOptions = [
		"Vehicle",
		"Machinery",
		"Implements",
		"Tool",
		"Other"
	];

	// Warranty period options
	const warrantyPeriodOptions = [
		"1 Year",
		"2 Years",
		"3 Years",
		"5 Years",
		"10 Years",
		"Lifetime",
		"Other"
	];

	// Unit of measure options
	const unitOfMeasureOptions = [
		"Hours",
		"Kms"
	];

	const currentReadingLabel = {
		"Hours": "Latest reading",
		"Kms": "Latest reading",
		"Month": "Last serviced",
	}[equipmentForm?.maintenanceType] || "Latest reading";

	// Fuel type options
	const fuelTypeOptions = [
		"Petrol",
		"Diesel",
		"Electric",
		"Hybrid",
		"LPG",
		"CNG",
		"Other"
	];

	// Site options for usual location
	const siteOptions = siteList
		.filter((site) => site.status && site.status.toLowerCase() === "active")
		.map((site) => ({
			label: site.siteName,
			value: site.id
		}))
		.sort((a, b) => (a.label > b.label ? 1 : -1));

	useEffect(() => {
		setEquipmentForm(equipment || {
			equipment: "",
			active: true,
			type: "",
			make: "",
			model: "",
			serialRego: "",
			vin: "",
			assetNumber: "",
			fuelType: "",
			purchaseDate: null,
			warrantyPeriod: "",
			usualLocationSiteId: "",
			maintenanceType: "",
			currentHoursKms: null,
			currentHoursKmsDate: null,
			nextWof: null,
			nextReg: null,
			nextRUCs: null,
			nextService: null,
			hourlyRate: null,
			attachments: [],
			save: { saving: false }
		});
	}, [equipment]);

	const onSelectDate = useCallback(
		(key) => (value) => {
			setEquipmentForm({
				...equipmentForm,
				[key]: value ? moment(value).format() : null
			});
		},
		[equipmentForm]
	);

	const handleCurrentHoursKmsChange = useCallback((e) => {
		const newValue = e.target.value ? parseFloat(e.target.value) : null;
		setEquipmentForm({
			...equipmentForm,
			currentHoursKms: newValue,
			currentHoursKmsDate: newValue !== equipmentForm?.currentHoursKms ? moment().format() : equipmentForm?.currentHoursKmsDate
		});
	}, [equipmentForm]);

	return (
		<Modal
			open={isOpen}
			onClose={onClose}
		>
			<Card
				className={clsx(classes.modalCard, undefined)}
			>
				<ValidatorForm
					autoComplete="off"
					noValidate
					onSubmit={() => {
						// Map frontend camelCase to backend PascalCase
						const mappedForm = {
							id: equipmentForm?.id,
						// Ensure organisationId is always sent for create/update
						organisationId: equipmentForm?.organisationId || currentOrganisationId,
							equipment: equipmentForm?.equipment,
							active: equipmentForm?.active,
							type: equipmentForm?.type,
							make: equipmentForm?.make,
							model: equipmentForm?.model,
							serialRego: equipmentForm?.serialRego,
							vin: equipmentForm?.vin,
							assetNumber: equipmentForm?.assetNumber,
							fuelType: equipmentForm?.fuelType,
							purchaseDate: equipmentForm?.purchaseDate,
							warrantyPeriod: equipmentForm?.warrantyPeriod,
							usualLocationSiteId: equipmentForm?.usualLocationSiteId || null,
							maintenanceType: equipmentForm?.maintenanceType,
							currentHoursKms: equipmentForm?.currentHoursKms,
							currentHoursKmsDate: equipmentForm?.currentHoursKmsDate,
							nextWof: equipmentForm?.nextWof,
							nextReg: equipmentForm?.nextReg,
							nextRUCs: equipmentForm?.nextRUCs,
							nextService: equipmentForm?.nextService,
							hourlyRate: equipmentForm?.hourlyRate,
							attachments: equipmentForm?.attachments || []
						};
						onSubmit(mappedForm);
					}}
				>
					<CardContent>
						<Typography
							align="center"
							gutterBottom
							variant="h3"
						>
							{readOnly ? "View" : equipment ? "Edit" : "Add"}{" "}
							Equipment
						</Typography>
						<Grid
							className={
								classes.modalGridContainer
							}
							container
							spacing={3}
						>
							{/* Basic Information */}
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="Equipment *"
									name="equipment"
									value={equipmentForm?.equipment || ""}
									variant="outlined"
									validators={readOnly ? [] : ["required"]}
									errorMessages={[
										"This field is required",
									]}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm(
											{
												...equipmentForm,
												equipment: e.target.value
											}
										);
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectValidator
									fullWidth
									id="type"
									name="type"
									label="Type *"
									select
									SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
									value={equipmentForm?.type || ""}
									variant="outlined"
									validators={readOnly ? [] : ["required"]}
									errorMessages={["This field is required"]}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											type: e.target.value || ""
										});
									}}
								>
									<MenuItem value="" disabled />
									{equipmentTypeOptions.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</SelectValidator>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="Make"
									name="make"
									value={equipmentForm?.make || ""}
									variant="outlined"
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											make: e.target.value
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="Model"
									name="model"
									value={equipmentForm?.model || ""}
									variant="outlined"
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											model: e.target.value
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="Serial / Rego"
									name="serialRego"
									value={equipmentForm?.serialRego || ""}
									variant="outlined"
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											serialRego: e.target.value
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="VIN"
									name="vin"
									value={equipmentForm?.vin || ""}
									variant="outlined"
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											vin: e.target.value
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextValidator
									fullWidth
									label="Asset Number"
									name="assetNumber"
									value={equipmentForm?.assetNumber || ""}
									variant="outlined"
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											assetNumber: e.target.value
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectInput
									id="fuelType"
									name="fuelType"
									label="Fuel Type"
									options={fuelTypeOptions}
									value={equipmentForm?.fuelType || ""}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											fuelType: e || ""
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<DatePicker
									fullWidth
									label="Purchase Date"
									format="Do MMMM YYYY"
									inputVariant="outlined"
									value={equipmentForm?.purchaseDate ? moment(equipmentForm.purchaseDate) : null}
									onChange={onSelectDate("purchaseDate")}
									disableFuture
									disabled={readOnly}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectInput
									id="warrantyPeriod"
									name="warrantyPeriod"
									label="Warranty Period"
									options={warrantyPeriodOptions}
									value={equipmentForm?.warrantyPeriod || ""}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											warrantyPeriod: e || ""
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectInput
									id="usualLocationSiteId"
									name="usualLocationSiteId"
									label="Usual Location (Site)"
									options={siteOptions}
									value={equipmentForm?.usualLocationSiteId || ""}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											usualLocationSiteId: e || ""
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectInput
									id="maintenanceType"
									name="maintenanceType"
									label="Unit of Measure"
									options={unitOfMeasureOptions}
									value={equipmentForm?.maintenanceType || ""}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											maintenanceType: e || ""
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								{equipmentForm?.maintenanceType !== "Month" && (
									<TextField
										fullWidth
										label={currentReadingLabel}
										name="currentHoursKms"
										type="number"
										value={equipmentForm?.currentHoursKms || ""}
										variant="outlined"
										disabled={readOnly}
										onChange={handleCurrentHoursKmsChange}
										helperText={equipmentForm?.currentHoursKmsDate ? `Last updated: ${moment(equipmentForm.currentHoursKmsDate).format("Do MMM YYYY")}` : ""}
									/>
								)}
							</Grid>
							<Grid item md={6} xs={12}>
								<DatePicker
									fullWidth
									label="Next WOF"
									format="Do MMMM YYYY"
									inputVariant="outlined"
									value={equipmentForm?.nextWof ? moment(equipmentForm.nextWof) : null}
									onChange={onSelectDate("nextWof")}
									disabled={readOnly}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<DatePicker
									fullWidth
									label="Next Reg"
									format="Do MMMM YYYY"
									inputVariant="outlined"
									value={equipmentForm?.nextReg ? moment(equipmentForm.nextReg) : null}
									onChange={onSelectDate("nextReg")}
									disabled={readOnly}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<DatePicker
									fullWidth
									label="Next RUCs"
									format="Do MMMM YYYY"
									inputVariant="outlined"
									value={equipmentForm?.nextRUCs ? moment(equipmentForm.nextRUCs) : null}
									onChange={onSelectDate("nextRUCs")}
									disabled={readOnly}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<DatePicker
									fullWidth
									label="Next Service"
									format="Do MMMM YYYY"
									inputVariant="outlined"
									value={equipmentForm?.nextService ? moment(equipmentForm.nextService) : null}
									onChange={onSelectDate("nextService")}
									disabled={readOnly}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextField
									fullWidth
									label="Hourly Rate"
									name="hourlyRate"
									type="number"
									value={equipmentForm?.hourlyRate || ""}
									variant="outlined"
									inputProps={{ step: "0.01" }}
									disabled={readOnly}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											hourlyRate: e.target.value ? parseFloat(e.target.value) : null
										});
									}}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectInput
									noNone
									id="status"
									name="status"
									label="Status"
									options={[
										"Active",
										"Inactive",
									]}
									value={
										equipmentForm?.active || !equipment ? "Active" : "Inactive"
									}
									disabled={
										readOnly || (equipment
											? false
											: true)
									}
									onChange={(e) => {
										setEquipmentForm({
											...equipmentForm,
											active: e === "Active" ? true : false
										});
									}}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={3}>
							<Grid item md={12} xs={12}>
								<UploadFormCard
									title="Add Photos / Documents"
									values={equipmentForm}
									onChange={(payload) =>
										setEquipmentForm({
											...equipmentForm,
											...payload
										})
									}
									disabled={readOnly}
								/>
							</Grid>
							{!readOnly && (
								<Grid item md={12} xs={12}>
									<FormActions
										className={classes.actions}
									>
										<FormSubmitButton
											disabled={false}
										>
											Save
										</FormSubmitButton>
										<FormButton
											onClick={() => onClose()}
										>
											Cancel
										</FormButton>
									</FormActions>
								</Grid>
							)}
							{readOnly && (
								<Grid item md={12} xs={12}>
									<FormActions
										className={classes.actions}
									>
										<FormButton
											onClick={() => onClose()}
										>
											Close
										</FormButton>
									</FormActions>
								</Grid>
							)}
						</Grid>
					</CardContent>
				</ValidatorForm>
			</Card>
		</Modal>
	);
};

EquipmentModal.propTypes = {
	equipment: PropTypes.object,
	isOpen: PropTypes.bool.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	readOnly: PropTypes.bool
};

export default EquipmentModal;

